import React, { createElement, Fragment, ReactElement, useCallback } from 'react';
import { useRouter } from 'next/router';
import useClassNames from '@loomstateorg/shared-frontend/hooks/useClassNames';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import Skeleton from '@loomstateorg/shared-frontend/components/commercetools-ui/atoms/skeleton';
import { TypographyProps } from './types';

const Typography: React.FC<TypographyProps> = ({
  medium = false,
  children,
  underline = false,
  className = '',
  translation,
  as = 'p',
  fontSize,
  align = 'left',
  fontFamily = 'inter',
  lineHeight = 'normal',
  asSkeleton = false,
  ...props
}) => {
  const router = useRouter();
  const { formatMessage } = useFormat({ name: translation?.file });

  const getContent = useCallback(() => {
    // Check if the children has different locales
    if (typeof children !== 'string') {
      // Update text based on locale
      const locale = router?.locale || router?.defaultLocale;
      return children?.[locale as string];
    }

    // Check if there is translation
    if (translation) {
      const content = formatMessage({ id: translation.id, defaultMessage: children });
      return content;
    }

    return children;
  }, [formatMessage, router?.defaultLocale, router?.locale, translation, children]);

  const fontFamiliesRef = {
    inter: 'body',
    libre: 'heading',
    crimson: 'heading',
    BarlowSemiCondensedThin: 'BarlowSemiCondensedThin',
    BarlowSemiCondensedSemiBold: 'BarlowSemiCondensedSemiBold',
    BarlowSemiCondensedExtraLight: 'BarlowSemiCondensedExtraLight',
    BarlowSemiCondensedLight: 'BarlowSemiCondensedLight',
    BarlowSemiCondensedRegular: 'BarlowSemiCondensedRegular',
    MontserratSemiBold: 'MontserratSemiBold',
    MontserratMedium: 'MontserratMedium',
    MontserratRegular: 'MontserratRegular',
    NunitoSansBold: 'NunitoSansBold',
    NunitoSansSemiBold: 'NunitoSansSemiBold',
    NunitoSansRegular: 'NunitoSansRegular',
    PoppinsRegular: 'PoppinsRegular',
    PoppinsSemiBold: 'PoppinsSemiBold',
    PoppinsBold: 'PoppinsBold',
    GothamMedium: 'GothamMedium',
    PoppinsExtraBold: 'PoppinsExtraBold',
    IBMPlexMonoBold: 'IBMPlexMonoBold',
    IBMPlexMonoSemiBold: 'IBMPlexMonoSemiBold',
    IBMPlexMonoRegular: 'IBMPlexMonoRegular',
    primaryRegular: 'primaryRegular',
    primaryThin: 'primaryThin',
    primaryBold: 'primaryBold',
    secondaryRegular: 'secondaryRegular',
    secondaryThin: 'secondaryThin',
    secondaryBold: 'secondaryBold',
  };

  // Constructing classnames of the element
  const elementClassName = useClassNames([
    { underline },
    align ? `text-${align}` : '',
    fontSize ? `text-${fontSize}` : '',
    lineHeight ? `leading-${lineHeight}` : lineHeight,
    { 'font-medium': medium },
    `font-${fontFamiliesRef[fontFamily]}`,
    { relative: asSkeleton },
    className,
  ]);

  // Constructing default props of the element
  const elementProps: ReactElement['props'] = {
    className: elementClassName,
    ...props,
  };

  const TypographyElement =
    as === 'fragment' ? (
      <>
        {asSkeleton && <Skeleton />}
        {getContent()}
      </>
    ) : (
      createElement(
        as as any,
        elementProps as any,
        <>
          {asSkeleton && <Skeleton />}
          {getContent()}
        </>,
      )
    );

  return TypographyElement;
};

export default Typography;
